<template>
  <div class="cookie-field">
    <div class="cookie-field__checkbox">
      <input
        type="checkbox"
        :id="id"
        :value="name"
        :checked="checked"
        :disabled="disabled"
        class="cookie-field__input"
        @change="$emit('change', $event.target.checked)"
      />
      <div class="cookie-field__sprites">
        <sprite id="app/checkbox-empty" class="cookie-field__sprite cookie-field__sprite--empty" />
        <sprite id="app/checkbox-checked" class="cookie-field__sprite cookie-field__sprite--checked" />
      </div>
    </div>
    <label :for="id" class="cookie-field__label">
      <h3 class="mb-2 font-bold text-h4">
        {{ $t(`${name}.label`) }}
      </h3>
      <div class="text-dark-grey text-base">
        {{ $t(`${name}.copy`) }}
      </div>
    </label>
  </div>
</template>

<script>
import Sprite from "@/shared/sprite"
import "/wds/sprites/app/checkbox-empty.svg"
import "/wds/sprites/app/checkbox-checked.svg"

export default {
  name: "CookieField",
  components: {
    Sprite
  },
  model: {
    prop: "checked",
    event: "change"
  },
  props: {
    name: { type: String, default: "" },
    checked: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },
  computed: {
    id() {
      return `cookie-${this.name}`
    }
  },
  i18n: {
    messages: {
      en: {
        necessary: {
          label: "Functional",
          copy: "They help make a website usable by enabling basic functions like page navigation, creating an account, signing in, or storing user privacy consent. Crucially, these technologies ensure the technical operation, security, accessibility, and protect against fraud and fraudulent bots on our websites and apps. The website cannot function properly without these cookies."
        },
        statistics: {
          label: "Statistical",
          copy: "They help us understand how visitors and customers utilize our platform, allowing us to analyze user behavior and interactions. This insight enables us to improve the performance, functionality and user experience of our websites, apps and services. They also gauge the effectiveness of our marketing efforts like advertisements and communications across various channels."
        },
        preferences: {
          label: "Preferences",
          copy: "They personalize your experience by remembering settings for language, currency, and other preferences to provide an efficient and effective user experience across platforms."
        },
        marketing: {
          label: "Marketing",
          copy: "Blinkist and our trusted partners collect information about user journeys through cookies and other tracking technologies, in order to deliver relevant sponsored content about our products. We may share this information and a customer identifier, like an encrypted e-mail address or device details, with third parties like Facebook and Google. We do this to provide you with relevant ads on third-party websites and apps and to improve our marketing. We may link this data across different devices you use, as well as process data about your interactions with ads to measure ad performance."
        }
      },
      de: {
        necessary: {
          label: "Funktional",
          copy: "Diese Cookies machen eine Website erst nutzbar. Sie ermöglichen grundlegende Funktionen wie die Seitennavigation, die Erstellung eines Kontos, die Anmeldung oder die Speicherung deiner Zustimmung zu den Datenschutzbestimmungen. Außerdem sorgen sie dafür, dass unsere Websites und Apps sicher und für alle zugänglich sind und schützen vor Betrug und betrügerischen Bots. Die Website funktioniert ohne diese Cookies nicht richtig."
        },
        statistics: {
          label: "Statistisch",
          copy: "Diese Cookies helfen uns zu verstehen, wie Besucher und Kunden unsere Plattform nutzen, und ermöglichen es uns, Nutzerverhalten und Interaktionen zu analysieren. Anhand dieser Informationen können wir die Leistung, Funktionalität und Benutzererfahrung unserer Websites, Anwendungen und Dienste verbessern. Sie helfen uns auch, die Wirksamkeit unserer Marketingmaßnahmen wie Werbung und Kommunikation über verschiedene Kanäle zu messen."
        },
        preferences: {
          label: "Präferenzen",
          copy: "Diese Cookies personalisieren deine Erfahrung, indem sie Einstellungen für Sprache, Währung und andere Präferenzen speichern. So wird eine effiziente und effektive Nutzererfahrung über alle Plattformen hinweg sichergestellt."
        },
        marketing: {
          label: "Marketing",
          copy: "Blinkist und unsere vertrauenswürdigen Partner sammeln Informationen über die Benutzerreisen mittels Cookies und anderer Tracking-Technologien, um dir relevante gesponserte Inhalte über unsere Produkte bereitzustellen. Wir können diese Informationen und eine Kundenkennung, wie eine verschlüsselte E-Mail-Adresse oder Gerätedetails, mit Dritten wie Facebook und Google teilen. Dies erfolgt, um dir relevante Anzeigen auf Websites und Apps von Drittanbietern bereitzustellen und unser Marketing zu optimieren. Zudem können wir diese Daten über verschiedene von dir verwendete Geräte hinweg verknüpfen und Daten zu deinen Interaktionen mit Anzeigen verarbeiten, um die Leistung von Anzeigen zu messen."
        }
      },
      es: {
        necessary: {
          label: "Funcionales",
          copy: "Ayudan a que un sitio web sea utilizable al habilitar funciones básicas como la navegación por páginas, la creación de una cuenta, el inicio de sesión o el almacenamiento del consentimiento de privacidad del usuario. Crucialmente, estas tecnologías garantizan el funcionamiento técnico, la seguridad, la accesibilidad y protegen contra el fraude y los bots fraudulentos en nuestros sitios web y aplicaciones. El sitio web no puede funcionar correctamente sin estas cookies."
        },
        statistics: {
          label: "Estadísticas",
          copy: "Nos ayudan a comprender cómo los visitantes y clientes utilizan nuestra plataforma, lo que nos permite analizar el comportamiento e interacciones de los usuarios. Esta información nos permite mejorar el rendimiento, la funcionalidad y la experiencia del usuario en nuestros sitios web, aplicaciones y servicios. También evalúan la efectividad de nuestros esfuerzos de marketing como anuncios y comunicaciones en varios canales."
        },
        preferences: {
          label: "Personalización",
          copy: "Personalizan tu experiencia recordando configuraciones de idioma, moneda y otras preferencias para proporcionar una experiencia de usuario eficiente y efectiva en todas las plataformas."
        },
        marketing: {
          label: "Marketing",
          copy: "Blinkist y nuestros socios de confianza recopilan información sobre los recorridos de los usuarios a través de cookies y otras tecnologías de seguimiento, con el fin de ofrecer contenido patrocinado relevante sobre nuestros productos. Podemos compartir esta información y un identificador de cliente, como una dirección de correo electrónico encriptada o detalles del dispositivo, con terceros como Facebook y Google. Hacemos esto para mostrarte anuncios relevantes en sitios web y aplicaciones de terceros y para mejorar nuestro marketing. Podemos vincular estos datos en diferentes dispositivos que uses, así como procesar datos sobre tus interacciones con anuncios para medir el rendimiento de los mismos."
        }
      }
    }
  }
}
</script>

<style lang="scss">
.cookie-field {
  $root: &;

  @include font-family(sans-serif);

  display: flex;
  align-content: flex-start;

  &__checkbox {
    position: relative;

    margin-right: $spacing-8;
  }

  input {
    position: absolute;
    z-index: 1;

    width: 100%;
    padding-top: 100%;

    opacity: 0;

    &:checked + #{$root}__sprites {
      #{$root}__sprite {
        &--empty {
          display: none;
        }

        &--checked {
          display: block;
        }
      }
    }

    &:disabled + #{$root}__sprites {
      color: $light-grey;
    }
  }

  &__sprite {
    &--checked {
      display: none;
    }
  }
}
</style>
